import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { syncUsersFromRemote, updateUser, deleteUser, getAllActivationRequests, getUserByPhone, getUserByUsername, creditReferralCommission, updateReferralCounts, type User, type ActivationRequest } from '../../../utils/sharedStorage';
import UserWithdrawals from './UserWithdrawals';

// ─── Modale détail / édition utilisateur ───────────────────────────────────
interface UserDetailModalProps {
  user: User;
  onClose: () => void;
  onSave: (phone: string, updates: Partial<User>) => Promise<void>;
}

function UserDetailModal({ user, onClose, onSave }: UserDetailModalProps) {
  const [form, setForm] = useState<Partial<User>>({
    name: user.name || '',
    email: user.email || '',
    country: user.country || '',
    username: user.username || '',
    password: user.password || '',
    referralCode: user.referralCode || '',
    referredBy: user.referredBy || '',
    accountStatus: user.accountStatus || 'pending',
    isActivated: user.isActivated || false,
    balance: user.balance || 0,
    points: user.points || 0,
    totalWithdrawals: user.totalWithdrawals || 0,
    level1Referrals: user.level1Referrals || 0,
    level2Referrals: user.level2Referrals || 0,
    level3Referrals: user.level3Referrals || 0,
    blocked: user.blocked || false,
    paymentMethod: user.paymentMethod || '',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'info' | 'finance' | 'parrainage' | 'retraits'>('info');

  const handleChange = (field: keyof User, value: unknown) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setSaved(false);
    setError('');
  };

  const handleSave = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!form.name?.trim()) { setError('Le nom est obligatoire.'); return; }
    if (!form.username?.trim()) { setError('Le username est obligatoire.'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave(user.phone, form);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Erreur sauvegarde:', err);
      setError('Erreur lors de la sauvegarde. Veuillez réessayer.');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-400 focus:border-transparent outline-none bg-white';
  const labelCls = 'block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide';

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-4" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-cyan-600 rounded-t-2xl px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xl font-bold">{(user.name || '?').charAt(0).toUpperCase()}</span>
            </div>
            <div>
              <h2 className="text-white font-bold text-lg leading-tight">{user.name || 'Utilisateur'}</h2>
              <p className="text-teal-100 text-xs">@{user.username} · {user.phone}</p>
            </div>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className="w-8 h-8 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full text-white transition cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Onglets */}
        <div className="flex border-b border-gray-200 px-6 pt-4 gap-1 overflow-x-auto">
          {([
            { key: 'info', label: 'Informations', icon: 'ri-user-line' },
            { key: 'finance', label: 'Finance', icon: 'ri-wallet-3-line' },
            { key: 'parrainage', label: 'Parrainage', icon: 'ri-team-line' },
            { key: 'retraits', label: 'Retraits', icon: 'ri-bank-card-line' },
          ] as const).map(t => (
            <button
              key={t.key}
              onClick={(e) => { e.stopPropagation(); setActiveTab(t.key); }}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-t-lg transition cursor-pointer whitespace-nowrap border-b-2 -mb-px ${
                activeTab === t.key
                  ? 'border-teal-500 text-teal-600 bg-teal-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className={`${t.icon} text-base`}></i>
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-6 space-y-5 max-h-[60vh] overflow-y-auto">

          {/* ── Onglet Informations ── */}
          {activeTab === 'info' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                  <p className="text-xs text-gray-500 mb-1">Statut du compte</p>
                  <select
                    value={form.accountStatus}
                    onChange={e => handleChange('accountStatus', e.target.value as User['accountStatus'])}
                    className={inputCls}
                  >
                    <option value="pending">En attente</option>
                    <option value="active">Actif</option>
                    <option value="inactive">Inactif</option>
                  </select>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                  <p className="text-xs text-gray-500 mb-1">Compte activé</p>
                  <select
                    value={form.isActivated ? 'true' : 'false'}
                    onChange={e => handleChange('isActivated', e.target.value === 'true')}
                    className={inputCls}
                  >
                    <option value="true">Oui</option>
                    <option value="false">Non</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Nom complet <span className="text-red-500">*</span></label>
                  <input type="text" value={form.name || ''} onChange={e => handleChange('name', e.target.value)} className={inputCls} placeholder="Nom complet" />
                </div>
                <div>
                  <label className={labelCls}>Username <span className="text-red-500">*</span></label>
                  <input type="text" value={form.username || ''} onChange={e => handleChange('username', e.target.value)} className={inputCls} placeholder="username" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Téléphone</label>
                  <input type="text" value={user.phone} disabled className={`${inputCls} bg-gray-100 text-gray-500 cursor-not-allowed`} />
                  <p className="text-xs text-gray-400 mt-1">Non modifiable (clé unique)</p>
                </div>
                <div>
                  <label className={labelCls}>Email</label>
                  <input type="email" value={form.email || ''} onChange={e => handleChange('email', e.target.value)} className={inputCls} placeholder="email@exemple.com" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Pays</label>
                  <input type="text" value={form.country || ''} onChange={e => handleChange('country', e.target.value)} className={inputCls} placeholder="Ex: Bénin" />
                </div>
                <div>
                  <label className={labelCls}>Méthode de paiement</label>
                  <input type="text" value={form.paymentMethod || ''} onChange={e => handleChange('paymentMethod', e.target.value)} className={inputCls} placeholder="Ex: MTN, Moov..." />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Mot de passe</label>
                  <input type="text" value={form.password || ''} onChange={e => handleChange('password', e.target.value)} className={inputCls} placeholder="Mot de passe" />
                </div>
                <div>
                  <label className={labelCls}>Code de parrainage</label>
                  <input type="text" value={form.referralCode || ''} onChange={e => handleChange('referralCode', e.target.value)} className={inputCls} placeholder="Code parrainage" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Bloqué</label>
                  <select
                    value={form.blocked ? 'true' : 'false'}
                    onChange={e => handleChange('blocked', e.target.value === 'true')}
                    className={inputCls}
                  >
                    <option value="false">Non</option>
                    <option value="true">Oui</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Date d'inscription</label>
                  <input type="text" value={user.registrationDate ? new Date(user.registrationDate).toLocaleString('fr-FR') : '—'} disabled className={`${inputCls} bg-gray-100 text-gray-500 cursor-not-allowed`} />
                </div>
              </div>

              {user.activatedAt && (
                <div>
                  <label className={labelCls}>Date d'activation</label>
                  <input type="text" value={new Date(user.activatedAt).toLocaleString('fr-FR')} disabled className={`${inputCls} bg-gray-100 text-gray-500 cursor-not-allowed`} />
                </div>
              )}
            </>
          )}

          {/* ── Onglet Finance ── */}
          {activeTab === 'finance' && (
            <>
              <div className="bg-gradient-to-r from-teal-500 to-cyan-500 rounded-xl p-4 text-white mb-2">
                <p className="text-xs font-semibold opacity-80 mb-1">💰 Solde actuel</p>
                <p className="text-3xl font-extrabold">{(user.balance || 0).toLocaleString()} F</p>
                <p className="text-xs opacity-70 mt-1">Modifiez le solde ci-dessous pour corriger une erreur</p>
              </div>

              <div className="grid grid-cols-3 gap-3 mb-2">
                <div className="bg-teal-50 border border-teal-200 rounded-xl p-3 text-center">
                  <p className="text-xs text-teal-600 font-semibold">Solde</p>
                  <p className="text-xl font-bold text-teal-700">{(user.balance || 0).toLocaleString()} F</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
                  <p className="text-xs text-amber-600 font-semibold">Points</p>
                  <p className="text-xl font-bold text-amber-700">{user.points || 0}</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-center">
                  <p className="text-xs text-red-600 font-semibold">Total retraits</p>
                  <p className="text-xl font-bold text-red-700">{(user.totalWithdrawals || 0).toLocaleString()} F</p>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 mb-3">
                <p className="text-xs text-yellow-800 font-semibold flex items-center gap-1">
                  <i className="ri-information-line text-base"></i>
                  Correction de solde — En cas d'erreur ou de réclamation
                </p>
                <p className="text-xs text-yellow-700 mt-1">Modifiez directement le solde de l'utilisateur. La modification est immédiate.</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Solde total (F) <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    value={form.balance ?? 0}
                    onChange={e => handleChange('balance', parseFloat(e.target.value) || 0)}
                    min="0"
                    step="1"
                    className={inputCls}
                  />
                  <p className="text-xs text-gray-400 mt-1">Solde global de l'utilisateur</p>
                </div>
                <div>
                  <label className={labelCls}>Points</label>
                  <input
                    type="number"
                    value={form.points ?? 0}
                    onChange={e => handleChange('points', parseInt(e.target.value) || 0)}
                    min="0"
                    step="1"
                    className={inputCls}
                  />
                </div>
              </div>

              <div>
                <label className={labelCls}>Total des retraits (F)</label>
                <input
                  type="number"
                  value={form.totalWithdrawals ?? 0}
                  onChange={e => handleChange('totalWithdrawals', parseFloat(e.target.value) || 0)}
                  min="0"
                  step="1"
                  className={inputCls}
                />
              </div>

              {user.lastLogin && (
                <div>
                  <label className={labelCls}>Dernière connexion</label>
                  <input type="text" value={new Date(user.lastLogin).toLocaleString('fr-FR')} disabled className={`${inputCls} bg-gray-100 text-gray-500 cursor-not-allowed`} />
                </div>
              )}
            </>
          )}

          {/* ── Onglet Parrainage ── */}
          {activeTab === 'parrainage' && (
            <>
              <div className="grid grid-cols-3 gap-3 mb-2">
                <div className="bg-teal-50 border border-teal-200 rounded-xl p-3 text-center">
                  <p className="text-xs text-teal-600 font-semibold">Niveau 1</p>
                  <p className="text-2xl font-bold text-teal-700">{user.level1Referrals || 0}</p>
                  <p className="text-xs text-teal-500">filleuls directs</p>
                </div>
                <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-3 text-center">
                  <p className="text-xs text-cyan-600 font-semibold">Niveau 2</p>
                  <p className="text-2xl font-bold text-cyan-700">{user.level2Referrals || 0}</p>
                  <p className="text-xs text-cyan-500">filleuls N2</p>
                </div>
                <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 text-center">
                  <p className="text-xs text-indigo-600 font-semibold">Niveau 3</p>
                  <p className="text-2xl font-bold text-indigo-700">{user.level3Referrals || 0}</p>
                  <p className="text-xs text-indigo-500">filleuls N3</p>
                </div>
              </div>

              <div>
                <label className={labelCls}>Parrainé par (username du parrain)</label>
                <input type="text" value={form.referredBy || ''} onChange={e => handleChange('referredBy', e.target.value)} className={inputCls} placeholder="Username du parrain" />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className={labelCls}>Filleuls N1</label>
                  <input type="number" value={form.level1Referrals ?? 0} onChange={e => handleChange('level1Referrals', parseInt(e.target.value) || 0)} min="0" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Filleuls N2</label>
                  <input type="number" value={form.level2Referrals ?? 0} onChange={e => handleChange('level2Referrals', parseInt(e.target.value) || 0)} min="0" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Filleuls N3</label>
                  <input type="number" value={form.level3Referrals ?? 0} onChange={e => handleChange('level3Referrals', parseInt(e.target.value) || 0)} min="0" className={inputCls} />
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1">
                  <i className="ri-qr-code-line text-base"></i>
                  Code de parrainage personnel
                </p>
                <div className="flex items-center gap-2">
                  <input type="text" value={form.referralCode || ''} onChange={e => handleChange('referralCode', e.target.value)} className={inputCls} />
                  <button
                    onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(form.referralCode || ''); }}
                    className="p-2 bg-teal-100 hover:bg-teal-200 text-teal-700 rounded-lg transition cursor-pointer flex-shrink-0"
                    title="Copier"
                  >
                    <i className="ri-file-copy-line text-base"></i>
                  </button>
                </div>
              </div>
            </>
          )}

          {/* ── Onglet Retraits ── */}
          {activeTab === 'retraits' && (
            <UserWithdrawals userPhone={user.phone} userName={user.name} />
          )}
        </div>

        {/* Footer */}
        {activeTab !== 'retraits' && (
          <div className="px-6 pb-6 pt-2 border-t border-gray-100">
            {error && (
              <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                <i className="ri-error-warning-line text-red-500"></i>
                <p className="text-xs text-red-600">{error}</p>
              </div>
            )}
            {saved && (
              <div className="mb-3 px-3 py-2 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                <i className="ri-checkbox-circle-line text-green-500"></i>
                <p className="text-xs text-green-700 font-semibold">✅ Modifications sauvegardées avec succès !</p>
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={(e) => { e.stopPropagation(); onClose(); }}
                className="flex-1 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl text-sm transition cursor-pointer whitespace-nowrap"
              >
                Fermer
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 py-2.5 bg-teal-600 hover:bg-teal-700 text-white font-bold rounded-xl text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow disabled:opacity-60"
              >
                {saving ? (
                  <><i className="ri-loader-4-line animate-spin text-base"></i>Sauvegarde...</>
                ) : (
                  <><i className="ri-save-line text-base"></i>Sauvegarder</>
                )}
              </button>
            </div>
          </div>
        )}
        {activeTab === 'retraits' && (
          <div className="px-6 pb-6 pt-2 border-t border-gray-100">
            <button
              onClick={(e) => { e.stopPropagation(); onClose(); }}
              className="w-full py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl text-sm transition cursor-pointer whitespace-nowrap"
            >
              Fermer
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Composant principal ────────────────────────────────────────────────────
export default function UserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [activationRequests, setActivationRequests] = useState<ActivationRequest[]>([]);
  const [filter, setFilter] = useState<'all' | 'active' | 'pending' | 'inactive' | 'validated-not-activated' | 'new'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const [activatingPhone, setActivatingPhone] = useState<string | null>(null);
  const isFetchingRef = useRef(false);

  const [detailUser, setDetailUser] = useState<User | null>(null);

  const [balanceModal, setBalanceModal] = useState<{ open: boolean; user: User | null }>({ open: false, user: null });
  const [balanceAmount, setBalanceAmount] = useState('');
  const [balanceType, setBalanceType] = useState<'credit' | 'debit'>('credit');
  const [balanceNote, setBalanceNote] = useState('');
  const [balanceError, setBalanceError] = useState('');
  const [isUpdatingBalance, setIsUpdatingBalance] = useState(false);

  const [deleteBelugaModal, setDeleteBelugaModal] = useState(false);
  const [isDeletingBeluga, setIsDeletingBeluga] = useState(false);

  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{ open: boolean; phone: string; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [deleteAllModal, setDeleteAllModal] = useState(false);
  const [isDeletingAll, setIsDeletingAll] = useState(false);

  const belugaUsers = useMemo(() => {
    return users.filter(u => {
      const name = (u.name || '').toLowerCase();
      const username = (u.username || '').toLowerCase();
      const email = (u.email || '').toLowerCase();
      return name.includes('beluga') || username.includes('beluga') || email.includes('beluga');
    });
  }, [users]);

  const handleDeleteBelugaUsers = async () => {
    setIsDeletingBeluga(true);
    try {
      for (const u of belugaUsers) {
        try { await deleteUser(u.phone); } catch { /* continuer */ }
      }
      // Supprimer aussi les demandes d'activation liées
      const belugaPhones = new Set(belugaUsers.map(u => u.phone));
      const allKeys = ['activationRequests', `SUCCESSLINK_V1__activationRequests`];
      for (const key of allKeys) {
        try {
          const raw = localStorage.getItem(key);
          if (raw) {
            const parsed = JSON.parse(raw);
            const filtered = parsed.filter((r: { userPhone: string }) => !belugaPhones.has(r.userPhone));
            localStorage.setItem(key, JSON.stringify(filtered));
          }
        } catch { /* ignore */ }
      }
      setUsers(prev => prev.filter(u => !belugaPhones.has(u.phone)));
      showSuccess(`✅ ${belugaUsers.length} compte(s) Beluga supprimé(s) avec succès !`);
      setDeleteBelugaModal(false);
    } catch (error) {
      console.error('Erreur suppression Beluga:', error);
    } finally {
      setIsDeletingBeluga(false);
    }
  };

  const isNewUser = (registrationDate: string) => {
    const now = Date.now();
    const regDate = new Date(registrationDate).getTime();
    const diffMinutes = (now - regDate) / 1000 / 60;
    return diffMinutes < 30;
  };

  const isToday = (registrationDate: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(registrationDate) >= today;
  };

  useEffect(() => {
    loadUsers();
    loadActivationRequests();
    // ── Polling toutes les 2 secondes ──
    const interval = setInterval(() => {
      if (!isFetchingRef.current) {
        loadUsers(true);
        loadActivationRequests();
      }
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const loadUsers = async (silent = false) => {
    if (isFetchingRef.current && silent) return;
    isFetchingRef.current = true;
    try {
      if (!silent) setLoading(true);
      const allUsers = await syncUsersFromRemote();
      // ✅ Filtrer les utilisateurs valides (avec un nom)
      const validUsers = allUsers.filter(u => u && u.phone);
      setUsers(validUsers);
    } catch (error) {
      console.error('Erreur chargement utilisateurs:', error);
    } finally {
      isFetchingRef.current = false;
      if (!silent) setLoading(false);
    }
  };

  const loadActivationRequests = async () => {
    try {
      const requests = await getAllActivationRequests();
      setActivationRequests(requests);
    } catch (error) {
      console.error('Erreur chargement demandes activation:', error);
    }
  };

  const handleBlockUser = async (phone: string) => {
    try {
      await updateUser(phone, { blocked: true });
      setUsers(prev => prev.map(u => u.phone === phone ? { ...u, blocked: true } : u));
      showSuccess('Utilisateur bloqué avec succès');
    } catch (error) {
      console.error('Erreur blocage utilisateur:', error);
    }
  };

  const handleUnblockUser = async (phone: string) => {
    try {
      await updateUser(phone, { blocked: false });
      setUsers(prev => prev.map(u => u.phone === phone ? { ...u, blocked: false } : u));
      showSuccess('Utilisateur débloqué avec succès');
    } catch (error) {
      console.error('Erreur déblocage utilisateur:', error);
    }
  };

  const handleDeactivateUser = async (phone: string, userName: string) => {
    try {
      await updateUser(phone, { accountStatus: 'inactive', isActivated: false });
      setUsers(prev => prev.map(u =>
        u.phone === phone ? { ...u, accountStatus: 'inactive', isActivated: false } : u
      ));
      localStorage.setItem(`deactivation_signal_${phone}`, JSON.stringify({ deactivated: true, deactivatedAt: new Date().toISOString() }));
      try {
        const ch = new BroadcastChannel('account_activation');
        ch.postMessage({ type: 'ACCOUNT_DEACTIVATED', phone, deactivatedAt: new Date().toISOString() });
        ch.close();
      } catch { /* ignore */ }
      showSuccess(`✅ Compte de ${userName} désactivé.`);
    } catch (error) {
      console.error('Erreur désactivation compte:', error);
    }
  };

  const confirmDeleteUser = (phone: string, name: string) => {
    setDeleteConfirmModal({ open: true, phone, name });
  };

  const handleDeleteUser = async () => {
    if (!deleteConfirmModal) return;
    setIsDeleting(true);
    try {
      await deleteUser(deleteConfirmModal.phone);
      setUsers(prev => prev.filter(u => u.phone !== deleteConfirmModal.phone));
      showSuccess(`Utilisateur ${deleteConfirmModal.name} supprimé avec succès`);
      setDeleteConfirmModal(null);
    } catch (error) {
      console.error('Erreur suppression utilisateur:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const confirmDeleteAll = () => setDeleteAllModal(true);

  const handleDeleteAll = async () => {
    setIsDeletingAll(true);
    try {
      const phonesToDelete = filteredUsers.map(u => u.phone);
      for (const phone of phonesToDelete) {
        try { await deleteUser(phone); } catch { /* continuer */ }
      }
      setUsers(prev => prev.filter(u => !phonesToDelete.includes(u.phone)));
      showSuccess(`✅ ${phonesToDelete.length} utilisateur(s) supprimé(s) avec succès`);
      setDeleteAllModal(false);
    } catch (error) {
      console.error('Erreur suppression en masse:', error);
    } finally {
      setIsDeletingAll(false);
    }
  };

  const handleSaveUserDetail = async (phone: string, updates: Partial<User>) => {
    await updateUser(phone, updates);
    setUsers(prev => prev.map(u => u.phone === phone ? { ...u, ...updates } : u));
    setDetailUser(prev => prev ? { ...prev, ...updates } : prev);
    // ✅ Mettre à jour currentUser si c'est l'utilisateur connecté
    try {
      const currentUserRaw = localStorage.getItem('currentUser');
      if (currentUserRaw) {
        const currentUser = JSON.parse(currentUserRaw);
        if (currentUser.phone === phone) {
          localStorage.setItem('currentUser', JSON.stringify({ ...currentUser, ...updates }));
        }
      }
    } catch { /* ignore */ }
    // ✅ Signal temps réel
    try {
      const ch = new BroadcastChannel('balance_update');
      ch.postMessage({ type: 'BALANCE_UPDATED', phone, timestamp: Date.now() });
      ch.close();
    } catch { /* ignore */ }
    showSuccess(`✅ Informations de l'utilisateur mises à jour avec succès !`);
  };

  const handleActivateAccount = async (phone: string, userName: string) => {
    setActivatingPhone(phone);
    try {
      const now = new Date().toISOString();
      await updateUser(phone, { accountStatus: 'active', isActivated: true, activatedAt: now });
      setUsers(prev => prev.map(u =>
        u.phone === phone ? { ...u, accountStatus: 'active', isActivated: true, activatedAt: now } : u
      ));
      localStorage.setItem(`activation_signal_${phone}`, JSON.stringify({ activated: true, activatedAt: now }));
      try {
        const channel = new BroadcastChannel('account_activation');
        channel.postMessage({ phone, activated: true, activatedAt: now });
        channel.close();
      } catch { /* ignore */ }
      try {
        const activatedUser = await getUserByPhone(phone);
        if (activatedUser?.referredBy) {
          // N1 — parrain direct
          await creditReferralCommission(activatedUser.referredBy, activatedUser.name, 1, 1500);
          await updateReferralCounts(activatedUser.referredBy);

          // ✅ Utiliser getUserByUsername car referredBy est un username
          const level1Referrer = await getUserByUsername(activatedUser.referredBy);
          if (level1Referrer) {
            try {
              const ch1 = new BroadcastChannel('balance_update');
              ch1.postMessage({ type: 'BALANCE_UPDATED', phone: level1Referrer.phone, timestamp: Date.now() });
              ch1.close();
            } catch { /* ignore */ }

            if (level1Referrer.referredBy) {
              // N2 — parrain du parrain
              await creditReferralCommission(level1Referrer.referredBy, activatedUser.name, 2, 600);
              await updateReferralCounts(level1Referrer.referredBy);

              // ✅ Utiliser getUserByUsername pour N2 aussi
              const level2Referrer = await getUserByUsername(level1Referrer.referredBy);
              if (level2Referrer) {
                try {
                  const ch2 = new BroadcastChannel('balance_update');
                  ch2.postMessage({ type: 'BALANCE_UPDATED', phone: level2Referrer.phone, timestamp: Date.now() });
                  ch2.close();
                } catch { /* ignore */ }

                if (level2Referrer.referredBy) {
                  // N3 — parrain du parrain du parrain
                  await creditReferralCommission(level2Referrer.referredBy, activatedUser.name, 3, 400);
                  await updateReferralCounts(level2Referrer.referredBy);
                }
              }
            }
          }
          showSuccess(`✅ Compte de ${userName} activé ! Commissions créditées (N1: +1500F, N2: +600F, N3: +400F).`);
        } else {
          showSuccess(`✅ Compte de ${userName} activé avec succès !`);
        }
      } catch {
        showSuccess(`✅ Compte de ${userName} activé avec succès !`);
      }
    } catch (error) {
      console.error('Erreur activation compte:', error);
      showSuccess("⚠️ Erreur lors de l'activation.");
    } finally {
      setActivatingPhone(null);
    }
  };

  const openBalanceModal = (user: User) => {
    setBalanceModal({ open: true, user });
    setBalanceAmount('');
    setBalanceType('credit');
    setBalanceNote('');
    setBalanceError('');
  };

  const closeBalanceModal = () => {
    setBalanceModal({ open: false, user: null });
    setBalanceAmount('');
    setBalanceType('credit');
    setBalanceNote('');
    setBalanceError('');
  };

  const confirmBalanceUpdate = async () => {
    if (!balanceModal.user) return;
    const amount = parseFloat(balanceAmount);
    if (isNaN(amount) || amount <= 0) { setBalanceError('Veuillez saisir un montant valide supérieur à 0'); return; }
    setIsUpdatingBalance(true);
    setBalanceError('');
    try {
      const user = balanceModal.user;
      const currentBalance = user.balance || 0;
      let newBalance = balanceType === 'credit' ? currentBalance + amount : currentBalance - amount;
      if (newBalance < 0) { setBalanceError('Le solde ne peut pas être négatif'); setIsUpdatingBalance(false); return; }
      await updateUser(user.phone, { balance: newBalance });
      setUsers(prev => prev.map(u => u.phone === user.phone ? { ...u, balance: newBalance } : u));
      try {
        const channel = new BroadcastChannel('balance_update');
        channel.postMessage({ type: 'BALANCE_UPDATED', phone: user.phone, newBalance, timestamp: Date.now() });
        channel.close();
      } catch { /* ignore */ }
      const action = balanceType === 'credit' ? 'crédité' : 'débité';
      const noteText = balanceNote.trim() ? ` (${balanceNote.trim()})` : '';
      showSuccess(`✅ Solde ${action} : ${amount.toLocaleString()} F${noteText}`);
      closeBalanceModal();
    } catch {
      setBalanceError('Erreur lors de la modification du solde.');
    } finally {
      setIsUpdatingBalance(false);
    }
  };

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 5000);
  };

  const validatedNotActivated = useMemo(() => {
    const validatedPhones = new Set(
      activationRequests.filter(req => req.status === 'approved').map(req => req.userPhone)
    );
    return users.filter(user => validatedPhones.has(user.phone) && (!user.isActivated || user.accountStatus === 'pending'));
  }, [users, activationRequests]);

  const validatedNotActivatedPhones = useMemo(() => new Set(validatedNotActivated.map(u => u.phone)), [validatedNotActivated]);

  const usersWithPaymentRequest = useMemo(() => {
    const phonesWithRequest = new Set(activationRequests.map(r => r.userPhone));
    return users.filter(u => phonesWithRequest.has(u.phone));
  }, [users, activationRequests]);

  const usersWithPaymentRequestPhones = useMemo(() => new Set(usersWithPaymentRequest.map(u => u.phone)), [usersWithPaymentRequest]);

  const newUsers = useMemo(() => users.filter(u => isNewUser(u.registrationDate)), [users]);
  const todayUsers = useMemo(() => users.filter(u => isToday(u.registrationDate)), [users]);

  // ✅ RECHERCHE CORRIGÉE — fonctionne sur nom, téléphone, username, email
  const filteredUsers = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return users.filter(user => {
      // Filtre par recherche
      if (q) {
        const name = (user.name || '').toLowerCase();
        const phone = (user.phone || '').toLowerCase();
        const username = (user.username || '').toLowerCase();
        const email = (user.email || '').toLowerCase();
        const country = (user.country || '').toLowerCase();
        if (!name.includes(q) && !phone.includes(q) && !username.includes(q) && !email.includes(q) && !country.includes(q)) {
          return false;
        }
      }
      // Filtre par statut
      switch (filter) {
        case 'active': return user.accountStatus === 'active' && user.isActivated;
        case 'pending': return user.accountStatus === 'pending' || !user.isActivated;
        case 'inactive': return user.accountStatus === 'inactive';
        case 'validated-not-activated': return validatedNotActivatedPhones.has(user.phone);
        case 'new': return isNewUser(user.registrationDate);
        default: return true;
      }
    });
  }, [users, searchTerm, filter, validatedNotActivatedPhones]);

  const counts = useMemo(() => ({
    all: users.length,
    active: users.filter(u => u.accountStatus === 'active' && u.isActivated).length,
    pending: users.filter(u => u.accountStatus === 'pending' || !u.isActivated).length,
    inactive: users.filter(u => u.accountStatus === 'inactive').length,
    validatedNotActivated: validatedNotActivated.length,
    new: newUsers.length,
    payment_clicked: usersWithPaymentRequest.length,
  }), [users, validatedNotActivated, newUsers, usersWithPaymentRequest]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3">
          <i className="ri-loader-4-line text-3xl text-teal-600 animate-spin"></i>
          <span className="text-gray-600">Chargement des utilisateurs...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center gap-2">
          <i className="ri-checkbox-circle-line text-xl"></i>
          <span>{successMessage}</span>
        </div>
      )}

      {/* Bannière Nouveaux inscrits */}
      {newUsers.length > 0 && (
        <div className="relative overflow-hidden bg-gradient-to-r from-teal-500 to-cyan-500 rounded-2xl p-5 shadow-lg">
          <div className="relative flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                <i className="ri-user-add-line text-3xl text-white"></i>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-white/20 text-white text-xs font-bold rounded-full animate-pulse">
                    <span className="w-1.5 h-1.5 bg-white rounded-full"></span>
                    EN DIRECT
                  </span>
                </div>
                <h3 className="text-white font-bold text-lg leading-tight">
                  🎉 {newUsers.length} nouveau{newUsers.length > 1 ? 'x' : ''} inscrit{newUsers.length > 1 ? 's' : ''} !
                </h3>
                <p className="text-teal-100 text-sm mt-0.5">
                  Dans les 30 dernières minutes · {todayUsers.length} aujourd'hui au total
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {newUsers.slice(0, 3).map(u => (
                <div key={u.phone} className="flex items-center gap-2 bg-white/15 rounded-xl px-3 py-2">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-sm">{(u.name || '?').charAt(0).toUpperCase()}</span>
                  </div>
                  <div>
                    <p className="text-white text-xs font-bold leading-tight">{u.name || u.phone}</p>
                    <p className="text-teal-100 text-xs">{u.country || u.phone}</p>
                  </div>
                </div>
              ))}
              {newUsers.length > 3 && (
                <div className="bg-white/15 rounded-xl px-3 py-2 text-white text-sm font-bold">
                  +{newUsers.length - 3} autres
                </div>
              )}
              <button
                onClick={() => setFilter('new')}
                className="px-4 py-2 bg-white text-teal-600 font-bold rounded-xl text-sm hover:bg-teal-50 transition cursor-pointer whitespace-nowrap flex items-center gap-2 shadow"
              >
                <i className="ri-eye-line"></i>
                Voir tous
              </button>
            </div>
          </div>
        </div>
      )}

      {validatedNotActivated.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-start gap-3">
          <i className="ri-alert-line text-2xl text-orange-600 mt-0.5"></i>
          <div className="flex-1">
            <h3 className="font-semibold text-orange-900 mb-1">
              ⚠️ {validatedNotActivated.length} compte{validatedNotActivated.length > 1 ? 's' : ''} en attente d'activation
            </h3>
            <p className="text-sm text-orange-700">Paiement validé mais compte non encore activé.</p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Gestion des utilisateurs</h2>
        <div className="flex items-center gap-3">
          <button onClick={() => loadUsers()} className="p-2 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition cursor-pointer" title="Actualiser">
            <i className="ri-refresh-line text-lg"></i>
          </button>
          {belugaUsers.length > 0 && (
            <button
              onClick={() => setDeleteBelugaModal(true)}
              className="px-3 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-semibold transition cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-user-unfollow-line"></i>
              Supprimer Beluga ({belugaUsers.length})
            </button>
          )}
          {filteredUsers.length > 0 && (
            <button
              onClick={confirmDeleteAll}
              className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold transition cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-delete-bin-line"></i>
              Supprimer tout ({filteredUsers.length})
            </button>
          )}
          <div className="text-sm text-gray-600">
            Total : <span className="font-semibold text-gray-900">{users.length}</span> utilisateurs
          </div>
        </div>
      </div>

      {/* ✅ BARRE DE RECHERCHE CORRIGÉE */}
      <div className="relative">
        <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl"></i>
        <input
          type="text"
          placeholder="Rechercher par nom, téléphone, username, email ou pays..."
          value={searchTerm}
          onChange={handleSearchChange}
          className="w-full pl-12 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm outline-none"
        />
        {searchTerm && (
          <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer">
            <i className="ri-close-circle-fill text-lg"></i>
          </button>
        )}
      </div>

      {searchTerm && (
        <p className="text-xs text-gray-500 -mt-3 ml-1 flex items-center gap-1">
          <i className="ri-filter-3-line"></i>
          <strong>{filteredUsers.length}</strong> résultat{filteredUsers.length > 1 ? 's' : ''} pour "<em>{searchTerm}</em>"
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        {([
          { key: 'all', label: 'Tous', count: counts.all, color: 'teal' },
          { key: 'new', label: '🆕 Nouveaux', count: counts.new, color: 'cyan' },
          { key: 'active', label: '✅ Actifs', count: counts.active, color: 'teal' },
          { key: 'pending', label: '⏳ En attente', count: counts.pending, color: 'teal' },
          { key: 'validated-not-activated', label: '⚠️ Validés non activés', count: counts.validatedNotActivated, color: 'orange' },
          { key: 'inactive', label: 'Inactifs', count: counts.inactive, color: 'teal' },
        ] as const).map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`relative px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap text-sm flex items-center gap-2 cursor-pointer ${
              filter === f.key
                ? f.color === 'orange' ? 'bg-orange-600 text-white'
                  : f.color === 'cyan' ? 'bg-cyan-600 text-white'
                  : 'bg-teal-600 text-white'
                : f.color === 'orange' ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                  : f.color === 'cyan' ? 'bg-cyan-100 text-cyan-700 hover:bg-cyan-200'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {f.label} ({f.count})
            {f.key === 'validated-not-activated' && f.count > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">{f.count}</span>
            )}
            {f.key === 'new' && f.count > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-teal-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">{f.count}</span>
            )}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Nom</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Téléphone</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Email</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Parrain</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Statut</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Solde</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Filleuls</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Inscription</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-gray-500">
                    <i className="ri-user-search-line text-4xl mb-2 block"></i>
                    {searchTerm ? `Aucun résultat pour "${searchTerm}"` : 'Aucun utilisateur trouvé'}
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => {
                  const isValidatedNotActivated = validatedNotActivatedPhones.has(user.phone);
                  const isActivating = activatingPhone === user.phone;
                  const isNew = isNewUser(user.registrationDate);
                  const isTodayUser = isToday(user.registrationDate);
                  return (
                    <tr key={user.phone} className={`hover:bg-gray-50 transition-colors ${isNew ? 'bg-teal-50/30' : ''}`}>

                      {/* ── Nom ── */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isNew ? 'bg-gradient-to-br from-teal-400 to-cyan-500 ring-2 ring-teal-300' : 'bg-teal-100'}`}>
                            <span className={`font-semibold text-xs ${isNew ? 'text-white' : 'text-teal-700'}`}>{(user.name || '?').charAt(0).toUpperCase()}</span>
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="font-semibold text-gray-900 text-sm">{user.name || <span className="text-gray-400 italic text-xs">Sans nom</span>}</span>
                              {isNew && (
                                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-teal-500 to-cyan-500 text-white">
                                  <i className="ri-flashlight-fill text-xs"></i>NEW
                                </span>
                              )}
                              {!isNew && isTodayUser && (
                                <span className="px-1.5 py-0.5 rounded-full text-xs font-semibold bg-teal-100 text-teal-700">Auj.</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-400">@{user.username || '—'}</div>
                          </div>
                        </div>
                      </td>

                      {/* ── Téléphone ── */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <i className="ri-phone-line text-gray-400 text-xs"></i>
                          <span className="text-sm font-medium text-gray-900">{user.phone}</span>
                        </div>
                        {user.country && (
                          <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                            <i className="ri-map-pin-line text-xs"></i>{user.country}
                          </div>
                        )}
                        {usersWithPaymentRequestPhones.has(user.phone) && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700 mt-1">
                            <i className="ri-bank-card-line text-xs"></i>Paiement initié
                          </span>
                        )}
                      </td>

                      {/* ── Email ── */}
                      <td className="px-4 py-3">
                        {user.email ? (
                          <div className="flex items-center gap-1 text-xs text-gray-600 max-w-[160px]">
                            <i className="ri-mail-line text-gray-400 flex-shrink-0"></i>
                            <span className="truncate">{user.email}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-300 italic">—</span>
                        )}
                      </td>

                      {/* ── Parrain ── */}
                      <td className="px-4 py-3">
                        {user.referredBy ? (
                          <div className="flex items-center gap-1">
                            <i className="ri-user-heart-line text-green-500 text-sm"></i>
                            <span className="text-sm font-semibold text-green-700">@{user.referredBy}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-300 italic">Aucun</span>
                        )}
                      </td>

                      {/* ── Statut ── */}
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          {isValidatedNotActivated ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-300">
                              <i className="ri-alert-line"></i>Validé non activé
                            </span>
                          ) : user.isActivated && user.accountStatus === 'active' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <i className="ri-checkbox-circle-line"></i>Actif
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              <i className="ri-time-line"></i>En attente
                            </span>
                          )}
                          {user.blocked && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              <i className="ri-forbid-line"></i>Bloqué
                            </span>
                          )}
                        </div>
                      </td>

                      {/* ── Solde ── */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-bold text-gray-900">{(user.balance || 0).toLocaleString()} F</span>
                          <button onClick={() => openBalanceModal(user)} className="p-1 text-teal-600 hover:bg-teal-50 rounded transition cursor-pointer" title="Modifier le solde">
                            <i className="ri-edit-line text-xs"></i>
                          </button>
                        </div>
                        {(user.points || 0) > 0 && (
                          <div className="text-xs text-amber-600">{user.points} pts</div>
                        )}
                      </td>

                      {/* ── Filleuls ── */}
                      <td className="px-4 py-3">
                        <div className="text-xs text-gray-600 space-y-0.5">
                          <div className="flex items-center gap-1">
                            <span className="w-4 h-4 flex items-center justify-center bg-teal-100 text-teal-700 rounded text-xs font-bold">1</span>
                            <span>{user.level1Referrals || 0}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="w-4 h-4 flex items-center justify-center bg-cyan-100 text-cyan-700 rounded text-xs font-bold">2</span>
                            <span>{user.level2Referrals || 0}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="w-4 h-4 flex items-center justify-center bg-indigo-100 text-indigo-700 rounded text-xs font-bold">3</span>
                            <span>{user.level3Referrals || 0}</span>
                          </div>
                        </div>
                      </td>

                      {/* ── Inscription ── */}
                      <td className="px-4 py-3">
                        <div className="text-xs text-gray-600">{user.registrationDate ? new Date(user.registrationDate).toLocaleDateString('fr-FR') : '—'}</div>
                        <div className="text-xs text-gray-400">{user.registrationDate ? new Date(user.registrationDate).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''}</div>
                        {user.activatedAt && (
                          <div className="text-xs text-green-600 mt-0.5">
                            <i className="ri-check-line"></i> {new Date(user.activatedAt).toLocaleDateString('fr-FR')}
                          </div>
                        )}
                      </td>

                      {/* ── Actions ── */}
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={(e) => { e.stopPropagation(); setDetailUser(JSON.parse(JSON.stringify(user))); }}
                            className="p-2 text-teal-600 hover:bg-teal-50 rounded-lg transition cursor-pointer"
                            title="Voir et modifier"
                          >
                            <i className="ri-eye-line text-base"></i>
                          </button>
                          {user.isActivated && user.accountStatus === 'active' && (
                            <button
                              onClick={() => handleDeactivateUser(user.phone, user.name)}
                              className="px-2.5 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-xs font-medium flex items-center gap-1 whitespace-nowrap cursor-pointer"
                            >
                              <i className="ri-user-unfollow-line"></i>
                              Désactiver
                            </button>
                          )}
                          {isValidatedNotActivated && (
                            <button
                              onClick={() => handleActivateAccount(user.phone, user.name)}
                              disabled={isActivating}
                              className="px-2.5 py-1.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-xs font-medium flex items-center gap-1 whitespace-nowrap cursor-pointer disabled:opacity-60"
                            >
                              {isActivating ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                              {isActivating ? '...' : 'Activer'}
                            </button>
                          )}
                          {!isValidatedNotActivated && (!user.isActivated || user.accountStatus === 'pending') && (
                            <button
                              onClick={() => handleActivateAccount(user.phone, user.name)}
                              disabled={isActivating}
                              className="px-2.5 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-xs font-medium flex items-center gap-1 whitespace-nowrap cursor-pointer disabled:opacity-60"
                            >
                              {isActivating ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-user-follow-line"></i>}
                              {isActivating ? '...' : 'Activer'}
                            </button>
                          )}
                          {user.blocked ? (
                            <button onClick={() => handleUnblockUser(user.phone)} className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors cursor-pointer" title="Débloquer">
                              <i className="ri-lock-unlock-line text-base"></i>
                            </button>
                          ) : (
                            <button onClick={() => handleBlockUser(user.phone)} className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors cursor-pointer" title="Bloquer">
                              <i className="ri-lock-line text-base"></i>
                            </button>
                          )}
                          <button onClick={() => confirmDeleteUser(user.phone, user.name)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer" title="Supprimer">
                            <i className="ri-delete-bin-line text-base"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modale détail utilisateur */}
      {detailUser && (
        <UserDetailModal
          key={detailUser.phone}
          user={JSON.parse(JSON.stringify(detailUser))}
          onClose={() => setDetailUser(null)}
          onSave={handleSaveUserDetail}
        />
      )}

      {/* Modale modification solde rapide */}
      {balanceModal.open && balanceModal.user && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0">
                <i className="ri-wallet-3-line text-2xl text-teal-600"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Modifier le solde</h3>
                <p className="text-sm text-gray-500"><strong>{balanceModal.user.name}</strong> (@{balanceModal.user.username})</p>
              </div>
            </div>
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-3 mb-4">
              <p className="text-xs text-teal-700 font-semibold flex items-center gap-1">
                <i className="ri-information-line text-base"></i>
                Solde actuel : <span className="font-bold">{(balanceModal.user.balance || 0).toLocaleString()} F</span>
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Type d'opération</label>
              <div className="flex gap-3">
                <button onClick={() => setBalanceType('credit')} className={`flex-1 py-2.5 rounded-lg font-semibold text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${balanceType === 'credit' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  <i className="ri-add-circle-line text-lg"></i>Créditer
                </button>
                <button onClick={() => setBalanceType('debit')} className={`flex-1 py-2.5 rounded-lg font-semibold text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${balanceType === 'debit' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  <i className="ri-subtract-line text-lg"></i>Débiter
                </button>
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Montant (F)</label>
              <input type="number" value={balanceAmount} onChange={(e) => { setBalanceAmount(e.target.value); setBalanceError(''); }} placeholder="Ex: 5000" min="0" step="1" className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-teal-400 focus:border-transparent outline-none" />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Note (optionnelle)</label>
              <textarea value={balanceNote} onChange={(e) => setBalanceNote(e.target.value)} rows={2} maxLength={200} placeholder="Ex: Correction erreur, bonus..." className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-teal-400 focus:border-transparent outline-none resize-none" />
            </div>
            {balanceError && (
              <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-xs text-red-600 flex items-center gap-1"><i className="ri-error-warning-line"></i> {balanceError}</p>
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={closeBalanceModal} disabled={isUpdatingBalance} className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl text-sm transition cursor-pointer whitespace-nowrap disabled:opacity-50">Annuler</button>
              <button onClick={confirmBalanceUpdate} disabled={isUpdatingBalance} className="flex-1 py-3 bg-teal-600 hover:bg-teal-700 text-white font-bold rounded-xl text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow disabled:opacity-50">
                {isUpdatingBalance ? (<><i className="ri-loader-4-line animate-spin text-lg"></i>Mise à jour...</>) : (<><i className="ri-check-line text-lg"></i>Confirmer</>)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale confirmation suppression individuelle */}
      {deleteConfirmModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full flex-shrink-0">
                <i className="ri-error-warning-line text-2xl text-red-600"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Confirmer la suppression</h3>
                <p className="text-sm text-gray-500">Cette action est irréversible</p>
              </div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-red-800">Supprimer <strong>{deleteConfirmModal.name}</strong> définitivement ?</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirmModal(null)} disabled={isDeleting} className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl text-sm transition cursor-pointer whitespace-nowrap disabled:opacity-50">Annuler</button>
              <button onClick={handleDeleteUser} disabled={isDeleting} className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow disabled:opacity-50">
                {isDeleting ? (<><i className="ri-loader-4-line animate-spin text-lg"></i>Suppression...</>) : (<><i className="ri-delete-bin-line text-lg"></i>Supprimer</>)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale suppression en masse */}
      {deleteAllModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full flex-shrink-0">
                <i className="ri-alarm-warning-line text-2xl text-red-600"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Suppression en masse</h3>
                <p className="text-sm text-gray-500">Action irréversible</p>
              </div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-red-800 font-semibold">⚠️ Supprimer <strong>{filteredUsers.length}</strong> utilisateur{filteredUsers.length > 1 ? 's' : ''} ?</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setDeleteAllModal(false)} disabled={isDeletingAll} className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl text-sm transition cursor-pointer whitespace-nowrap disabled:opacity-50">Annuler</button>
              <button onClick={handleDeleteAll} disabled={isDeletingAll} className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow disabled:opacity-50">
                {isDeletingAll ? (<><i className="ri-loader-4-line animate-spin text-lg"></i>Suppression...</>) : (<><i className="ri-delete-bin-line text-lg"></i>Tout supprimer</>)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale suppression comptes Beluga */}
      {deleteBelugaModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-full flex-shrink-0">
                <i className="ri-alarm-warning-line text-2xl text-orange-600"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Supprimer les comptes Beluga</h3>
                <p className="text-sm text-gray-500">Action irréversible</p>
              </div>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-orange-800 font-semibold mb-2">
                ⚠️ Vous allez supprimer <strong>{belugaUsers.length}</strong> compte(s) contenant "beluga" :
              </p>
              <ul className="space-y-1 max-h-32 overflow-y-auto">
                {belugaUsers.map(u => (
                  <li key={u.phone} className="text-xs text-orange-700 flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    <strong>{u.name}</strong> (@{u.username}) — {u.phone}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-orange-600 mt-2">Leurs demandes de paiement seront également supprimées.</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteBelugaModal(false)}
                disabled={isDeletingBeluga}
                className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl text-sm transition cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteBelugaUsers}
                disabled={isDeletingBeluga}
                className="flex-1 py-3 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-xl text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow disabled:opacity-50"
              >
                {isDeletingBeluga ? (
                  <><i className="ri-loader-4-line animate-spin text-lg"></i>Suppression...</>
                ) : (
                  <><i className="ri-delete-bin-line text-lg"></i>Supprimer Beluga</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
